import { Capacitor } from "@capacitor/core";

const PRODUCTION_HOSTS = new Set(["voltyshare.com", "www.voltyshare.com"]);

export const PRODUCTION_SUPABASE_PROJECT_ID = "kjfkfhjanbaampoggpbf";
export const PRODUCTION_SUPABASE_URL = `https://${PRODUCTION_SUPABASE_PROJECT_ID}.supabase.co`;

// Supabase publishable/anon keys are browser credentials, not service secrets.
// Lovable production builds can miss VITE_* env after removing tracked .env;
// keep production web bootable without reintroducing .env into git.
export const PRODUCTION_SUPABASE_PUBLISHABLE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtqZmtmaGphbmJhYW1wb2dncGJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQxOTk4MzMsImV4cCI6MjA4OTc3NTgzM30.fKB3t5UeuFt9VQO4QYl16aNuNIh2YVrkYIqzUaJ4PZk";

const cleanEnvValue = (value: unknown) => (typeof value === "string" ? value.trim() : "");

export type PublicSupabaseEnv = {
  VITE_APP_ENV?: unknown;
  VITE_SUPABASE_URL?: unknown;
  VITE_SUPABASE_PUBLISHABLE_KEY?: unknown;
  VITE_SUPABASE_PROJECT_ID?: unknown;
};

const getBrowserHostname = () => (typeof window !== "undefined" ? window.location.hostname : "");
const isNativeRuntime = () => typeof window !== "undefined" && Capacitor.isNativePlatform();

export const isProductionSupabaseFallbackHost = (hostname: string | undefined) =>
  Boolean(hostname && PRODUCTION_HOSTS.has(hostname));

const isExplicitNonProductionBuild = (env: PublicSupabaseEnv) => {
  const appEnv = cleanEnvValue(env.VITE_APP_ENV).toLowerCase();
  return Boolean(appEnv && appEnv !== "production");
};

export const resolvePublicSupabaseConfig = (
  hostname = getBrowserHostname(),
  env: PublicSupabaseEnv = import.meta.env as unknown as PublicSupabaseEnv,
  nativeRuntime = isNativeRuntime(),
) => {
  // A Lovable preview must declare its own VITE_SUPABASE_* values. Treating a
  // preview hostname as production would turn an unfinished staging session
  // into a production-data mutation path.
  // Native production builds run through localhost, but a native staging build
  // must fail closed when its explicit staging config is incomplete.
  const useProductionFallback =
    !isExplicitNonProductionBuild(env) && (nativeRuntime || isProductionSupabaseFallbackHost(hostname));

  return {
    url: cleanEnvValue(env.VITE_SUPABASE_URL) || (useProductionFallback ? PRODUCTION_SUPABASE_URL : ""),
    publishableKey:
      cleanEnvValue(env.VITE_SUPABASE_PUBLISHABLE_KEY) ||
      (useProductionFallback ? PRODUCTION_SUPABASE_PUBLISHABLE_KEY : ""),
    projectId:
      cleanEnvValue(env.VITE_SUPABASE_PROJECT_ID) ||
      (useProductionFallback ? PRODUCTION_SUPABASE_PROJECT_ID : ""),
  };
};

export const getSupabaseUrl = () => resolvePublicSupabaseConfig().url;

export const getSupabasePublishableKey = () => resolvePublicSupabaseConfig().publishableKey;

export const hasPublicSupabaseConfig = (
  hostname = getBrowserHostname(),
  env: PublicSupabaseEnv = import.meta.env as unknown as PublicSupabaseEnv,
  nativeRuntime = isNativeRuntime(),
) => {
  const { url, publishableKey } = resolvePublicSupabaseConfig(hostname, env, nativeRuntime);
  return Boolean(url && publishableKey);
};

export const getSupabaseProjectId = () => resolvePublicSupabaseConfig().projectId;

export const getSupabaseFunctionUrl = (functionName: string) => {
  const supabaseUrl = getSupabaseUrl().replace(/\/$/, "");
  return supabaseUrl ? `${supabaseUrl}/functions/v1/${functionName}` : "";
};
